
import React from "react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Building2, RefreshCw } from "lucide-react";
import { getMagazijnLabel } from "@/types/inventory";
import { Button } from "@/components/ui/button";

interface WarehouseFilterProps {
  availableWarehouses: number[];
  selectedWarehouse: number | null;
  onChange: (value: number | null) => void;
  onRefresh?: () => Promise<void>;
}

export const WarehouseFilter = ({ 
  availableWarehouses, 
  selectedWarehouse, 
  onChange,
  onRefresh 
}: WarehouseFilterProps) => {
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Define standard warehouses
  const standardWarehouses = [2, 5, 7, 12]; // Goes, Dordrecht, PC, Breda
  
  // Handle refresh button click
  const handleRefresh = async () => {
    if (!onRefresh) return;
    
    setIsRefreshing(true);
    try {
      await onRefresh();
      console.log('Warehouses refreshed');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Ensure we always have our standard warehouses in the list
  const ensureStandardWarehouses = () => {
    const warehouses = [...availableWarehouses];
    
    // Add any missing standard warehouses
    standardWarehouses.forEach(warehouse => {
      if (!warehouses.includes(warehouse)) {
        warehouses.push(warehouse);
      }
    });
    
    // Return sorted
    return [...warehouses].sort((a, b) => a - b);
  };

  const displayedWarehouses = ensureStandardWarehouses();

  // Handle the case when no warehouses are available
  if (!availableWarehouses || availableWarehouses.length === 0) {
    return (
      <div className="flex flex-col space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <label className="text-sm text-muted-foreground">Filter op magazijn</label>
          </div>
          {onRefresh && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Select disabled value="all">
            <SelectTrigger className="bg-slate-50">
              <SelectValue placeholder="Geen magazijnen beschikbaar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Geen magazijnen beschikbaar</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <p className="text-xs text-amber-600">
          Geen magazijnen gevonden in de database
        </p>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="mt-1"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
          Standaard magazijnen laden
        </Button>
      </div>
    );
  }

  // Convert warehouse to proper value for the component
  const selectedValue = selectedWarehouse !== null ? selectedWarehouse.toString() : "all";
  
  console.log('WarehouseFilter rendering with:', {
    availableWarehouses: displayedWarehouses,
    selectedWarehouse,
    selectedValue,
    warehousesCount: displayedWarehouses.length
  });

  return (
    <div className="flex flex-col space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <label className="text-sm text-muted-foreground">Filter op magazijn</label>
        </div>
        {onRefresh && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Magazijnen opnieuw laden"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        )}
      </div>
      
      <Select 
        value={selectedValue}
        onValueChange={(value) => {
          console.log('WarehouseFilter onValueChange called with:', value, 'type:', typeof value);
          if (value === "all") {
            onChange(null);
          } else {
            const warehouseNumber = parseInt(value, 10);
            if (!isNaN(warehouseNumber)) {
              console.log('Passing warehouse number to onChange:', warehouseNumber, 'type:', typeof warehouseNumber);
              onChange(warehouseNumber);
            } else {
              console.error('Failed to parse warehouse number:', value);
              onChange(null);
            }
          }
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Alle magazijnen" />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          <SelectItem value="all" className="font-bold">Alle magazijnen</SelectItem>
          
          {/* Display all warehouses sorted numerically with better key handling */}
          {displayedWarehouses
            .sort((a, b) => a - b)
            .map((warehouse) => (
              <SelectItem 
                key={`warehouse-${warehouse}`} 
                value={warehouse.toString()}
              >
                {getMagazijnLabel(warehouse)}
              </SelectItem>
            ))
          }
        </SelectContent>
      </Select>
      
      <p className="text-xs text-muted-foreground">
        {displayedWarehouses.length} magazijnen beschikbaar
      </p>
    </div>
  );
};
